/** Tool selection. Every change is pushed straight into the engine's ghosts. */

import { BRACKET_SK_LENGTHS } from "../../data/haki.ts";
import type { AccessKind, DeckKind, RailKind, ToolId } from "../../engine/tools/types.ts";
import type { BeamKind, BracketKind, PlankFamily } from "../../model/types.ts";
import { getEngine } from "../engine/engine.actions.ts";
import { setHud } from "../hud/hud.actions.ts";
import { type ToolState, toolState } from "./tool.state.ts";

/** Selectable lengths per tool, millimetres. Null means the chips are hidden. */
export const TOOL_SIZES: Readonly<Record<ToolId, readonly number[] | null>> = {
  nav: null,
  move: null,
  spir: [3000, 2000, 1500, 1000, 500],
  beam: [3050, 2500, 1964, 1655, 1250, 1050, 770, 700],
  diag: null,
  deck: null,
  bracket: null,
  access: [3050, 2500, 1500, 1000, 500],
  rail: null,
  plane: null,
  del: null,
};

export const TOOL_TIPS: Readonly<Record<ToolId, string>> = {
  nav: "Navigate — click planks to remove / put back",
  move: "Move — click any part to select the whole scaffold it belongs to, then drag the gizmo's arrows. Standards joined by beams, diagonals or brackets travel together, so a second, unconnected scaffold stays put. Snapping aligns it to the scaffold you drop it against. Click empty space or press Esc to deselect.",
  spir: "Standard — click a surface to place a standard (selected length). Click an existing standard to stack the selected length on top.",
  beam: "Beam — click a cup group, aim at another standard and click: the length adjusts to the module that fits. With no standard in the aim, the selected length is placed with a free end.",
  diag: "Diagonal — click the lower cup, then the upper cup on another standard",
  deck: "Deck — Whole bay: hover a closed bay near the beam the planks should rest on; they span from it to the opposite beam (the stripes show it), click to pack the bay with the chosen boards; click a plank to take it out. Single plank: hover near the beam the board should rest on and click to lay one board in that slot.",
  bracket:
    "Bracket (konsoll) — every free outer face of a standard at a lift with beams shows in blue. SK 770 hooks the cup group and carries its own arm; SKD 1250 braces a 1250 beam from the cup group 1.5 m below. Both end in a cup group + spigot: beam between the brackets, deck the bay, then guardrail — the 1 m standard on the bracket is added for you.",
  rail: "Guardrail — every beam edge without a frame / toe board shows in blue. Click to add the chosen combination; click an existing frame or toe board to remove it.",
  access:
    "Access — Hatch deck + ladder: click a decked bay. UTV ×2000: needs two parallel transoms c/c 2500/3050 with the upper +2.0 m (beams first!). 500/1000/1500 hook on one beam edge.",
  plane:
    "Plane — a flat surface you add yourself, where the model has none: click a plane to select it, then drag the gizmo to move it (the green arrow is its height), resize it or rotate it. Standards stand on planes like on a roof. Add planes here in Options or in the Planes panel.",
  del: "Delete — the part to be deleted glows red when you hover it. Right-click also deletes in every other tool.",
};

function push(next: ToolState): void {
  toolState.set(next);
  getEngine().setSettings(next);
}

/** ALP board widths a single board can be laid in, millimetres. */
const BOARD_WIDTHS: readonly number[] = [230, 170];

/** Keep the current size when the tool offers it, else fall back to its first. */
function withSize(next: ToolState): ToolState {
  const sizes = sizesFor(next);
  const size = sizes && !sizes.includes(next.size) ? (sizes[0] ?? next.size) : next.size;

  return { ...next, size };
}

/** Sizes are per-tool; keep the current one when the new tool offers it. */
export function setTool(tool: ToolId): void {
  push(withSize({ ...toolState.get(), tool }));
  setHud(TOOL_TIPS[tool]);
}

/**
 * Which lengths a tool offers. Only the UTV stairs come in sizes among the
 * access parts, only the SK brackets among the bracket kinds — the SKD is
 * fixed at 1250 by the beam it combines with — and the Deck tool offers the
 * board width when laying single boards.
 */
export function sizesFor(
  state: Pick<ToolState, "tool" | "accKind" | "bracketKind" | "deckKind">,
): readonly number[] | null {
  const { tool, accKind, bracketKind, deckKind } = state;
  if (tool === "access" && accKind !== "utv") {
    return null;
  }
  if (tool === "bracket") {
    return bracketKind === "sk" ? BRACKET_SK_LENGTHS : null;
  }
  if (tool === "deck") {
    return deckKind === "board" ? BOARD_WIDTHS : null;
  }

  return TOOL_SIZES[tool];
}

export function setSize(size: number): void {
  push({ ...toolState.get(), size });
}

export function setBeamKind(beamKind: BeamKind): void {
  push({ ...toolState.get(), beamKind });
}

export function setAccessKind(accKind: AccessKind): void {
  push(withSize({ ...toolState.get(), accKind }));
}

export function setRailKind(railKind: RailKind): void {
  push({ ...toolState.get(), railKind });
}

export function setBracketKind(bracketKind: BracketKind): void {
  push(withSize({ ...toolState.get(), bracketKind }));
}

export function setDeckKind(deckKind: DeckKind): void {
  push(withSize({ ...toolState.get(), deckKind }));
}

export function setPlankFam(plankFam: PlankFamily): void {
  push({ ...toolState.get(), plankFam });
}

export function setAutoSpir(autoSpir: boolean): void {
  push({ ...toolState.get(), autoSpir });
}

export function setSnap(snap: boolean): void {
  push({ ...toolState.get(), snap });
}
